import {
    // ChildPriceRule,
    GuestTaxConfig,
    GuestTaxConfigChild,
    GuestTaxConfigAdult,
    InvoiceItemsModel,
    PriceOffer,
    PriceOfferInterval,
    ReservationEditModel,
    ReservationModelWithModelsIncluded,
    TaxModel,
} from '@common/modelDefinition';
import { Reservation } from './resController';
import dayjs from 'dayjs';

// ============================================================================
// INTERFACES AND TYPES
// ============================================================================

export interface CondoReservationCheck {
    isReservationMadeByOwner: boolean;
    isReservationMadeForOwner: boolean;
    isCondoReservation: boolean;
}

export interface OfferMismatch {
    invoiceItems: InvoiceItemsModel[];
    proformaInvoiceItems: InvoiceItemsModel[];
    reservations: ReservationEditModel[];
}

// AŽURIRANO: DefaultPosItem type - dodano minAge/maxAge
export type DefaultPosItem = {
    posItemId: number;
    posItemName: string;
    posItemPrice: number;
    quantityMode: '1' | 'perDay' | 'perGuest' | 'perDayPerGuest';
    applyToAdults?: boolean;
    applyToChildren?: boolean;
    type?: string | null;
    minAge?: number | null;  // NOVO
    maxAge?: number | null;  // NOVO
};

// NOVO: Dropdown opcija - može biti Tax ili PosItem
export interface DropdownOption {
    id: number;
    name: string;
    sourceType: 'tax' | 'posItem';
    minAge?: number | null;
    maxAge?: number | null;
    price?: number;
}

// NOVO: Rezultat sugestije - vraća ID i tip
export interface SuggestedItem {
    id: number | null;
    sourceType: 'tax' | 'posItem' | null;
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const _selfServiceCheckInTemplate = {
    pinCode: null,
    selfCheckInType: 'hardware', //hardware or sofware
    notificationSent: false,
    selfCheckInValidFrom: new Date().getTime(),
    selfCheckInValidTo: new Date(new Date().getTime() + 5 * 60 * 60 * 1000).getTime(),
    sendSelfCheckInCodeAfterTimestamp: new Date().getTime(),
    notificationLanguage: '',
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const getHourAndMinuteObj = (time: string) => {
    const newTime = time ? time.split(':') : [12, 0];
    const hour = Number(newTime[0]);
    const minute = Number(newTime[1]);
    return { hour, minute };
};

export const getQuery = (location: Location) => {
    let query: any = {};
    location.search
        .replace('?', '')
        .split('&')
        .forEach((param) => {
            const temp = param.split('=');
            query[temp[0]] = temp[1];
        });
    return query;
};

export const getCheckInAndCheckoutTimestamps = (location: Location, otherSettings: any) => {
    const query = getQuery(location);
    const { checkInTime = '12:00', checkOutTime = '12:00' } = otherSettings;
    const checkInHourAndMinute = getHourAndMinuteObj(checkInTime);
    const checkOutHourAndMinute = getHourAndMinuteObj(checkOutTime);

    const defaultCheckIn = new Date(
        new Date().setHours(checkInHourAndMinute.hour, checkInHourAndMinute.minute)
    ).getTime();

    const defaultCheckOut =
        new Date(new Date().setHours(checkOutHourAndMinute.hour, checkOutHourAndMinute.minute)).getTime() +
        24 * 60 * 60 * 1000;

    const checkInTimestamp = query.from
        ? new Date(
              new Date(parseInt(query.from, 10)).setHours(checkInHourAndMinute.hour, checkInHourAndMinute.minute)
          ).getTime()
        : defaultCheckIn;

    const checkOutTimestamp = query.to
        ? new Date(
              new Date(parseInt(query.to, 10)).setHours(checkOutHourAndMinute.hour, checkOutHourAndMinute.minute)
          ).getTime()
        : defaultCheckOut;

    return { checkInTimestamp, checkOutTimestamp };
};

// ============================================================================
// CONDO STATUS
// ============================================================================

export const condoStatusCheck = (reservation: Reservation | ReservationEditModel): CondoReservationCheck => {
    if (reservation) {
        const _isReservationMadeByOwner =
            reservation.receptionistUuid &&
            reservation.condoUserUuid &&
            reservation.receptionistUuid === reservation.condoUserUuid;
        const _isReservationMadeForOwner =
            reservation.condoUserUuid &&
            reservation.receptionistUuid &&
            reservation.receptionistUuid !== reservation.condoUserUuid;
        const result: CondoReservationCheck = {
            isReservationMadeByOwner: _isReservationMadeByOwner ? _isReservationMadeByOwner : false,
            isReservationMadeForOwner: _isReservationMadeForOwner ? _isReservationMadeForOwner : false,
            isCondoReservation: _isReservationMadeByOwner === true || _isReservationMadeForOwner === true,
        };
        return result;
    } else {
        return {
            isReservationMadeByOwner: false,
            isReservationMadeForOwner: false,
            isCondoReservation: false,
        };
    }
};

// ============================================================================
// INVOICE / OFFER MISMATCH FUNCTIONS
// ============================================================================

export function sumByNettoUnitPriceAndQuantity(items: InvoiceItemsModel[]): number {
    return items.reduce((sum, i) => {
        return sum + Number(i.nettoUnitPrice) * Number(i.quantity);
    }, 0);
}

export function checkOfferMismatch(params: OfferMismatch) {
    const { invoiceItems, proformaInvoiceItems, reservations } = params;
    let mismatchOffers: string[] = [];
    
    const accommodationInvoiceItems = invoiceItems.filter(
        (i: InvoiceItemsModel) => i.type === 'accommodation' && !i.posItemId
    );
    const accommodationProformaInvoiceItems = proformaInvoiceItems.filter(
        (i: InvoiceItemsModel) => i.type === 'accommodation' && !i.posItemId
    );

    // Usporedi TOTALE s tolerancijom za floating point greške
    const currentTotal = sumByNettoUnitPriceAndQuantity(accommodationInvoiceItems);
    const proformaTotal = sumByNettoUnitPriceAndQuantity(accommodationProformaInvoiceItems);
    
    if (Math.abs(currentTotal - proformaTotal) > 0.01) {
        mismatchOffers.push('totalValue');
    }

    // Refreshment stay provjera (postojeća logika)
    if (reservations.length > 0) {
        for (const reservation of reservations) {
            if (reservation && reservation.rememberedAccommodationPriceInfo) {
                const { rememberedAccommodationPriceInfo = [] } = reservation;
                const lastOffer =
                    rememberedAccommodationPriceInfo.length > 0
                        ? rememberedAccommodationPriceInfo[rememberedAccommodationPriceInfo.length - 1]
                        : null;
                        
                if (lastOffer) {
                    const { checkInTimestamp, checkOutTimestamp } = lastOffer;
                    
                    if (
                        dayjs(checkInTimestamp).isSame(checkOutTimestamp, 'day') &&
                        !dayjs(reservation.checkOutTimestamp).isSame(checkOutTimestamp, 'day')
                    ) {
                        mismatchOffers.push('refreshment stay detected');
                    }
                }
            }
        }
    }
    
    // UKLONJENA guest dates provjera - nepotrebna jer se totali već uspoređuju
    // Guest dates utječu na CIJENU, a cijena se već provjerava gore
    
    return mismatchOffers;
}

export function checkReservationFromGroupOfferMismatch(
    rememberedPriceOfferIntervals: PriceOfferInterval[],
    lastOffer: PriceOffer,
    reservation: ReservationModelWithModelsIncluded
) {
    const {
        accommodationPriceListId,
        checkInTimestamp,
        checkOutTimestamp,
        adultsNumber,
        childrenNumber,
        previousGroupUuid,
    } = reservation;

    const lastOfferIntervals = lastOffer.priceOfferIntervals;
    if (
        accommodationPriceListId !== lastOffer.accommodationPriceListId ||
        checkInTimestamp !== lastOffer.checkInTimestamp ||
        checkOutTimestamp !== lastOffer.checkOutTimestamp ||
        adultsNumber !== lastOffer.adultsNumber ||
        childrenNumber !== lastOffer.childrenNumber ||
        previousGroupUuid ||
        nestedSumByAPRules(rememberedPriceOfferIntervals) !== nestedSumByAPRules(lastOfferIntervals)
    ) {
        return true;
    } else {
        return false;
    }
}

function nestedSumByAPRules(intervals: PriceOfferInterval[]): number {
    return intervals.reduce((total, interval) => {
        const innerSum = interval.apRules.reduce((sum, rule) => {
            return sum + Number(rule);
        }, 0);
        return total + innerSum;
    }, 0);
}

// ============================================================================
// RESERVATION TOTAL PRICE
// ============================================================================

/**
 * Dohvati ukupnu cijenu rezervacije
 * 
 * - Nove rezervacije: koristi `totalPrice` (backend izračunao)
 * - Stare rezervacije: računa iz `apRules` (stara logika, bez children pricing-a)
 * 
 * @param reservation - Rezervacija
 * @returns Ukupna cijena smještaja
 */
export const getReservationTotalPrice = (reservation: Reservation | any): number => {
    // OpenPrice ima prioritet
    if (reservation.openPrice && Number(reservation.openPrice) > 0) {
        return Number(reservation.openPrice);
    }

    const offer = reservation.rememberedAccommodationPriceInfo?.[
        reservation.rememberedAccommodationPriceInfo.length - 1
    ];

    if (!offer) {
        return 0;
    }

    // NOVA LOGIKA: Backend je izračunao totalPrice
    if (offer.totalPrice !== undefined && offer.totalPrice !== null) {
        return Number(offer.totalPrice);
    }

    // FALLBACK za stare rezervacije: jednostavna logika (bez children pricing-a)
    const priceOfferIntervals = offer.priceOfferIntervals || [];
    
    if (priceOfferIntervals.length === 0) {
        return 0;
    }

    const board = reservation.board || 'bb';
    const adultsNumber = reservation.adultsNumber || 1;
    let total = 0;

    priceOfferIntervals.forEach((interval: any) => {
        const { apRules = [], fromToDays = 0, boards, defaultBoard } = interval;

        if (apRules.length === 0 || fromToDays <= 0) {
            return;
        }

        // Cijena za odrasle (stara logika)
        let priceIndex = adultsNumber - 1;
        if (priceIndex >= apRules.length) {
            priceIndex = apRules.length - 1;
        }
        if (priceIndex < 0) {
            priceIndex = 0;
        }

        let unitPrice = apRules[priceIndex] || 0;

        // Board delta
        if (boards && board && defaultBoard && board !== defaultBoard) {
            const boardDelta = boards[board] || 0;
            unitPrice += boardDelta * adultsNumber;
        }

        total += unitPrice * fromToDays;
    });

    return total;
};

// ============================================================================
// DROPDOWN OPTIONS - TAKSE + POS ARTIKLI
// ============================================================================

/**
 * Dohvati opcije za dropdown (takse + POS artikli)
 * 
 * Prikazuje SVE dostupne opcije - i takse i POS artikle.
 * Takse se prikazuju prve (sortirane po minAge), zatim POS artikli.
 * 
 * @param taxes - Sve takse iz store-a
 * @param defaultPosItems - POS artikli iz invoicingSettings
 * @param columnType - 'tax' za boravišnu pristojbu, 'insurance' za osiguranje
 * @param guestType - 'adult' ili 'child'
 * @returns Array dropdown opcija
 */
export const getDropdownOptions = (
    taxes: TaxModel[],
    defaultPosItems: DefaultPosItem[],
    columnType: 'tax' | 'insurance',
    guestType: 'adult' | 'child'
): DropdownOption[] => {
    const options: DropdownOption[] = [];

    // 1. TAKSE - filtriraj po tipu i aktivnosti
    const relevantTaxes = taxes
        .filter((t: TaxModel) => 
            t.type === columnType && 
            t.fixedAmount && 
            Number(t.isActive) === 1
        )
        .sort((a: TaxModel, b: TaxModel) => {
            const aMin = a.minAge !== null && a.minAge !== undefined ? a.minAge : 0;
            const bMin = b.minAge !== null && b.minAge !== undefined ? b.minAge : 0;
            return aMin - bMin;
        });

    // Dodaj takse u opcije
    for (const tax of relevantTaxes) {
        if (tax.id) {
            options.push({
                id: tax.id,
                name: tax.name || '',
                sourceType: 'tax',
                minAge: tax.minAge,
                maxAge: tax.maxAge,
                price: Number(tax.fixedAmount) || 0,
            });
        }
    }

    // 2. POS ARTIKLI - dodaj sve koji odgovaraju tipu i guestType
    const relevantPosItems = defaultPosItems.filter((item: DefaultPosItem) => {
        // Filtriraj po tipu (tax ili insurance)
        if (item.type !== columnType) return false;

        // Filtriraj po guestType (adult ili child)
        if (guestType === 'adult' && item.applyToAdults !== true) return false;
        if (guestType === 'child' && item.applyToChildren !== true) return false;

        return true;
    });

    // NOVO: Sortiraj POS artikle po minAge
    const sortedPosItems = relevantPosItems.sort((a: DefaultPosItem, b: DefaultPosItem) => {
        const aMin = a.minAge !== null && a.minAge !== undefined ? a.minAge : 0;
        const bMin = b.minAge !== null && b.minAge !== undefined ? b.minAge : 0;
        return aMin - bMin;
    });

    for (const posItem of sortedPosItems) {
        // Provjeri da POS artikal nije duplikat (isti naziv kao neka taksa)
        const isDuplicate = options.some(opt => opt.name === posItem.posItemName);
        if (!isDuplicate) {
            options.push({
                id: posItem.posItemId,
                name: posItem.posItemName || '',
                sourceType: 'posItem',
                minAge: posItem.minAge || null,  // AŽURIRANO: Koristi minAge iz POS artikla
                maxAge: posItem.maxAge || null,  // AŽURIRANO: Koristi maxAge iz POS artikla
                price: posItem.posItemPrice || 0,
            });
        }
    }

    return options;
};

/**
 * Provjeri ima li opcija za prikaz dropdowna
 */
export const hasDropdownOptions = (
    taxes: TaxModel[],
    defaultPosItems: DefaultPosItem[],
    columnType: 'tax' | 'insurance',
    guestType: 'adult' | 'child'
): boolean => {
    return getDropdownOptions(taxes, defaultPosItems, columnType, guestType).length > 0;
};

/**
 * Odredi sourceType na osnovu odabranih opcija
 * Ako su takse dostupne, koristi 'tax', inače 'posItem'
 */
export const determineSourceType = (
    taxes: TaxModel[],
    defaultPosItems: DefaultPosItem[],
    columnType: 'tax' | 'insurance'
): 'tax' | 'posItem' | null => {
    const hasTaxes = taxes.some((t: TaxModel) => 
        t.type === columnType && 
        t.fixedAmount && 
        Number(t.isActive) === 1
    );

    if (hasTaxes) return 'tax';

    const hasPosItems = defaultPosItems.some((item: DefaultPosItem) => 
        item.type === columnType
    );

    if (hasPosItems) return 'posItem';

    return null;
};

// ============================================================================
// GUEST TAX CONFIG FUNCTIONS
// ============================================================================

/**
 * Parse guestTaxConfig iz rezervacije
 * Vraća parsed config ili null ako ne postoji
 * 
 * NAPOMENA: Prima CIJELU REZERVACIJU, ne samo guestTaxConfig!
 */
export const parseGuestTaxConfig = (reservation: Reservation | any): GuestTaxConfig | null => {
    if (!reservation) return null;
    
    const { guestTaxConfig } = reservation;
    
    if (!guestTaxConfig) {
        return null;
    }
    
    if (typeof guestTaxConfig === 'string') {
        try {
            return JSON.parse(guestTaxConfig);
        } catch {
            return null;
        }
    }
    
    return guestTaxConfig as GuestTaxConfig;
};

/**
 * Predloži taksu prema dobi gosta (samo za Tax tablicu)
 * 
 * Koristi se na instancama koje koriste Tax model za boravišnu pristojbu.
 * NE koristi POS artikle - za to koristi getSuggestedItemByAge.
 */
export const getSuggestedTaxIdByAge = (
    customerAge: number, 
    taxType: 'tax' | 'insurance', 
    taxes: TaxModel[]
): number | null => {
    const relevantTaxes = taxes
        .filter((t: TaxModel) => t.type === taxType && t.fixedAmount && Number(t.isActive) === 1)
        .sort((a: TaxModel, b: TaxModel) => {
            const aMin = a.minAge !== null && a.minAge !== undefined ? a.minAge : 0;
            const bMin = b.minAge !== null && b.minAge !== undefined ? b.minAge : 0;
            return aMin - bMin;
        });

    for (const tax of relevantTaxes) {
        const minAge = tax.minAge !== null && tax.minAge !== undefined ? tax.minAge : 0;
        const maxAge = tax.maxAge !== null && tax.maxAge !== undefined ? tax.maxAge : Infinity;

        if (customerAge >= minAge && customerAge < maxAge) {
            return tax.id || 0;
        }
    }

    return null;
};

/**
 * Predloži stavku (Tax ili PosItem) prema dobi gosta
 * 
 * Prioritet:
 * 1. Taksa koja odgovara dobi (minAge/maxAge)
 * 2. POS artikal koji odgovara dobi (minAge/maxAge)
 * 3. POS artikal bez dobnih ograničenja (applyToAdults/applyToChildren)
 * 4. Prva taksa kao fallback
 * 5. null ako nema ničega
 * 
 * @param customerAge - Dob gosta
 * @param columnType - 'tax' za boravišnu pristojbu, 'insurance' za osiguranje
 * @param taxes - Sve takse iz store-a
 * @param defaultPosItems - POS artikli iz invoicingSettings
 * @param guestType - 'adult' ili 'child'
 * @returns { id, sourceType } ili { null, null }
 */
export const getSuggestedItemByAge = (
    customerAge: number,
    columnType: 'tax' | 'insurance',
    taxes: TaxModel[],
    defaultPosItems: DefaultPosItem[],
    guestType: 'adult' | 'child'
): SuggestedItem => {
    // 1. Prvo pokušaj pronaći odgovarajuću TAKSU prema dobi
    const relevantTaxes = taxes
        .filter((t: TaxModel) => t.type === columnType && t.fixedAmount && Number(t.isActive) === 1)
        .sort((a: TaxModel, b: TaxModel) => {
            const aMin = a.minAge !== null && a.minAge !== undefined ? a.minAge : 0;
            const bMin = b.minAge !== null && b.minAge !== undefined ? b.minAge : 0;
            return aMin - bMin;
        });

    for (const tax of relevantTaxes) {
        const minAge = tax.minAge !== null && tax.minAge !== undefined ? tax.minAge : 0;
        const maxAge = tax.maxAge !== null && tax.maxAge !== undefined ? tax.maxAge : Infinity;

        if (customerAge >= minAge && customerAge < maxAge) {
            return { id: tax.id || null, sourceType: 'tax' };
        }
    }

    // 2. Ako nema odgovarajuće takse PO DOBI, provjeri POS artikle
    const relevantPosItems = defaultPosItems.filter((item: DefaultPosItem) => {
        if (item.type !== columnType) return false;
        if (guestType === 'adult' && item.applyToAdults !== true) return false;
        if (guestType === 'child' && item.applyToChildren !== true) return false;
        return true;
    });

    // NOVO: Sortiraj POS artikle po minAge
    const sortedPosItems = relevantPosItems.sort((a: DefaultPosItem, b: DefaultPosItem) => {
        const aMin = a.minAge !== null && a.minAge !== undefined ? a.minAge : 0;
        const bMin = b.minAge !== null && b.minAge !== undefined ? b.minAge : 0;
        return aMin - bMin;
    });

    // NOVO: Prvo pokušaj pronaći POS artikal koji odgovara DOBI
    for (const posItem of sortedPosItems) {
        const minAge = posItem.minAge !== null && posItem.minAge !== undefined ? posItem.minAge : 0;
        const maxAge = posItem.maxAge !== null && posItem.maxAge !== undefined ? posItem.maxAge : Infinity;

        // Ako ima definiran minAge ILI maxAge, provjeri dob
        if (posItem.minAge !== null || posItem.maxAge !== null) {
            if (customerAge >= minAge && customerAge < maxAge) {
                return { id: posItem.posItemId, sourceType: 'posItem' };
            }
        }
    }

    // FALLBACK: Ako nema POS artikla po dobi, vrati prvi bez dobnih ograničenja
    const posItemWithoutAgeRestriction = sortedPosItems.find((item: DefaultPosItem) => {
        return item.minAge === null && item.maxAge === null;
    });

    if (posItemWithoutAgeRestriction) {
        return { id: posItemWithoutAgeRestriction.posItemId, sourceType: 'posItem' };
    }

    // 3. NEMA FALLBACKA NA PRVU TAKSU!
    // Ako customer ne matchira NIJEDNU taksu po dobi (već smo provjerili gore),
    // i nema POS artikla koji matchira, vraćamo null = "Bez naknade"
    // 
    // Prethodni bug: fallback je vraćao prvu taksu (sortiranu po minAge),
    // što je za dijete od 5 godina vraćalo umanjenu taksu (12-18) umjesto "Bez naknade"

    return { id: null, sourceType: null };
};

/**
 * Generiraj default guestTaxConfig na osnovu rezervacije
 * AŽURIRANO: Podržava POS artikle i fixedTaxType
 */
export const generateDefaultGuestTaxConfig = (
    reservation: Reservation | any,
    taxes: TaxModel[],
    defaultPosItems: DefaultPosItem[] = []
): GuestTaxConfig => {
    const totalGuests = reservation.adultsNumber || 1;
    const childrenCount = reservation.childrenNumber || 0;
    const adultsCount = Math.max(totalGuests - childrenCount, 1);
    
    const adults: GuestTaxConfigAdult[] = [];
    const children: GuestTaxConfigChild[] = [];
    
    const holderVisaInfo = reservation.VisaInfo?.find((v: any) => v.customerId === reservation.customerId);
    const holderCustomer = reservation.Guests?.find((g: any) => g.id === reservation.customerId);
    
    let holderAge = 30;
    if (holderCustomer?.dateOfBirth) {
        holderAge = dayjs().diff(holderCustomer.dateOfBirth, 'years');
    }

    const holderTax = getSuggestedItemByAge(holderAge, 'tax', taxes, defaultPosItems, 'adult');
    const holderInsurance = getSuggestedItemByAge(holderAge, 'insurance', taxes, defaultPosItems, 'adult');
    const holderTaxInfo1 = getTaxAmountInfoLocal(holderVisaInfo?.fixedTaxId || holderTax.id, holderVisaInfo?.fixedTaxType || holderTax.sourceType, taxes, defaultPosItems);
    const holderTaxInfo2 = getTaxAmountInfoLocal(holderVisaInfo?.fixedTaxId2 || holderInsurance.id, holderVisaInfo?.fixedTaxType || holderTax.sourceType, taxes, defaultPosItems);
    
    adults.push({
        visaInfoId: holderVisaInfo?.id || null,
        fixedTaxId: holderVisaInfo?.fixedTaxId || holderTax.id,
        fixedTaxId2: holderVisaInfo?.fixedTaxId2 || holderInsurance.id,
        fixedTaxType: holderVisaInfo?.fixedTaxType || holderTax.sourceType,
        fixedTaxAmount1: holderTaxInfo1.amount,
        fixedTaxAmount2: holderTaxInfo2.amount,
        fixedTaxName1: holderTaxInfo1.name,
        fixedTaxName2: holderTaxInfo2.name,
        checkInTimestamp: reservation.checkInTimestamp,
        checkOutTimestamp: reservation.checkOutTimestamp,
        isHolder: true,
    });
    
    for (let i = 1; i < adultsCount; i++) {
        const adultTax = getSuggestedItemByAge(30, 'tax', taxes, defaultPosItems, 'adult');
        const adultInsurance = getSuggestedItemByAge(30, 'insurance', taxes, defaultPosItems, 'adult');
        const taxInfo1 = getTaxAmountInfoLocal(adultTax.id, adultTax.sourceType, taxes, defaultPosItems);
        const taxInfo2 = getTaxAmountInfoLocal(adultInsurance.id, adultInsurance.sourceType, taxes, defaultPosItems);
        
        adults.push({
            visaInfoId: null,
            fixedTaxId: adultTax.id,
            fixedTaxId2: adultInsurance.id,
            fixedTaxType: adultTax.sourceType,
            fixedTaxAmount1: taxInfo1.amount,
            fixedTaxAmount2: taxInfo2.amount,
            fixedTaxName1: taxInfo1.name,
            fixedTaxName2: taxInfo2.name,
            checkInTimestamp: reservation.checkInTimestamp,
            checkOutTimestamp: reservation.checkOutTimestamp,
        });
    }
    
    for (let i = 0; i < childrenCount; i++) {
        const childTax = getSuggestedItemByAge(5, 'tax', taxes, defaultPosItems, 'child');
        const childInsurance = getSuggestedItemByAge(5, 'insurance', taxes, defaultPosItems, 'child');
        const taxInfo1 = getTaxAmountInfoLocal(childTax.id, childTax.sourceType, taxes, defaultPosItems);
        const taxInfo2 = getTaxAmountInfoLocal(childInsurance.id, childInsurance.sourceType, taxes, defaultPosItems);
        
        children.push({
            age: -1,
            visaInfoId: null,
            fixedTaxId: childTax.id,
            fixedTaxId2: childInsurance.id,
            fixedTaxType: childTax.sourceType,
            fixedTaxAmount1: taxInfo1.amount,
            fixedTaxAmount2: taxInfo2.amount,
            fixedTaxName1: taxInfo1.name,
            fixedTaxName2: taxInfo2.name,
            checkInTimestamp: reservation.checkInTimestamp,
            checkOutTimestamp: reservation.checkOutTimestamp,
        });
    }
    
    return { adults, children };
};

/**
 * Provjeri treba li guestConfig pažnju (nedostaje dob djece, itd.)
 */
export const checkIfGuestConfigNeedsAttention = (reservation: Reservation): boolean => {
    const config = parseGuestTaxConfig(reservation);
    if (!config) return false;
    
    const totalGuests = reservation.adultsNumber || 1;
    const childrenCount = reservation.childrenNumber || 0;
    const adultsCount = Math.max(totalGuests - childrenCount, 1);
    
    if (config.adults.length !== adultsCount) return true;
    if (config.children.length !== childrenCount) return true;
    
    for (const child of config.children) {
        if (child.visaInfoId === null && (child.age === 5 || child.age === 1)) {
            return true;
        }
    }
    
    return false;
};

/**
 * Provjeri ima li djece bez postavljene dobi
 * 
 * NAPOMENA: Prima CIJELU REZERVACIJU, ne samo guestTaxConfig!
 */
export const hasChildrenWithoutAge = (reservation: Reservation | any): boolean => {
    const config = parseGuestTaxConfig(reservation);
    if (!config?.children || config.children.length === 0) return false;
    return config.children.some((c: GuestTaxConfigChild) => c.age < 0);
};

/**
 * Dohvati dobi djece iz config-a
 * 
 * NAPOMENA: Prima CIJELU REZERVACIJU, ne samo guestTaxConfig!
 */
export const getChildrenAges = (reservation: Reservation | any): number[] => {
    const config = parseGuestTaxConfig(reservation);
    if (!config?.children) return [];
    return config.children.map((c: GuestTaxConfigChild) => c.age);
};

/**
 * Pronađi opciju u dropdownu po ID-u i sourceType-u
 */
export const findDropdownOption = (
    options: DropdownOption[],
    id: number | null,
    sourceType: 'tax' | 'posItem' | null
): DropdownOption | undefined => {
    if (!id) return undefined;
    return options.find(opt => opt.id === id && opt.sourceType === sourceType);
};

/**
 * Generiraj ključ za dropdown value (kombinacija id + sourceType)
 */
export const getDropdownValue = (id: number | null, sourceType: 'tax' | 'posItem' | null): string => {
    if (!id) return '0';
    return `${sourceType || 'tax'}_${id}`;
};

/**
 * Parsiraj dropdown value nazad u id i sourceType
 * 
 * VAŽNO: 
 * - "0" (Bez naknade) vraća id: 0 (NE null!)
 * - null/undefined znači "nije postavljeno"
 * - 0 znači "eksplicitno odabrano Bez naknade"
 */
export const parseDropdownValue = (value: string): { id: number | null; sourceType: 'tax' | 'posItem' | null } => {
    if (!value || value === '0') {
        // "Bez naknade" - vraća 0, ne null!
        return { id: 0, sourceType: null };
    }
    
    const parts = value.split('_');
    if (parts.length === 2) {
        return {
            sourceType: parts[0] as 'tax' | 'posItem',
            id: Number(parts[1]) || null,
        };
    }
    
    // Fallback za stare vrijednosti (samo broj)
    return {
        id: Number(value) || null,
        sourceType: 'tax',
    };
};

/**
 * Popuni SAMO nedostajuće (null/undefined) vrijednosti u guestTaxConfig
 * NE dira postojeće vrijednosti (uključujući 0 = "Bez naknade")
 */
export const fillMissingGuestTaxConfig = (
    reservation: Reservation | any,
    taxes: TaxModel[],
    defaultPosItems: DefaultPosItem[]
): GuestTaxConfig | null => {
    const totalGuests = reservation.adultsNumber || 1;
    const childrenCount = reservation.childrenNumber || 0;
    const adultsCount = Math.max(totalGuests - childrenCount, 1);
    
    let currentConfig = parseGuestTaxConfig(reservation);
    let hasChanges = false;
    
    if (!currentConfig) {
        return generateDefaultGuestTaxConfig(reservation, taxes, defaultPosItems);
    }
    
    const newConfig: GuestTaxConfig = {
        adults: currentConfig.adults.map(a => ({ ...a })),
        children: currentConfig.children.map(c => ({ ...c })),
    };
    
    // Dodaj nedostajuće ADULTS slotove
    while (newConfig.adults.length < adultsCount) {
        const suggestedTax = getSuggestedItemByAge(30, 'tax', taxes, defaultPosItems, 'adult');
        const suggestedInsurance = getSuggestedItemByAge(30, 'insurance', taxes, defaultPosItems, 'adult');
        newConfig.adults.push({
            visaInfoId: null,
            fixedTaxId: suggestedTax.id,
            fixedTaxId2: suggestedInsurance.id,
            fixedTaxType: suggestedTax.sourceType,
            checkInTimestamp: reservation.checkInTimestamp,
            checkOutTimestamp: reservation.checkOutTimestamp,
        });
        hasChanges = true;
    }
    
    // Popuni SAMO null/undefined za postojeće odrasle
    for (let i = 0; i < newConfig.adults.length; i++) {
        const adult = newConfig.adults[i];
        
        // Samo ako je null/undefined (NE ako je 0!)
        if (adult.fixedTaxId === null || adult.fixedTaxId === undefined) {
            const suggested = getSuggestedItemByAge(30, 'tax', taxes, defaultPosItems, 'adult');
            // SAMO ako ima što postaviti!
            if (suggested.id !== null && suggested.id !== undefined) {
                newConfig.adults[i].fixedTaxId = suggested.id;
                newConfig.adults[i].fixedTaxType = suggested.sourceType;
                hasChanges = true;
            }
        }
        
        // Ako ima ID ali nema type
        if (adult.fixedTaxId && !adult.fixedTaxType) {
            const isPosItem = defaultPosItems.some(p => p.posItemId === adult.fixedTaxId);
            newConfig.adults[i].fixedTaxType = isPosItem ? 'posItem' : 'tax';
            hasChanges = true;
        }
    }
    
    // Dodaj nedostajuće CHILDREN slotove
    while (newConfig.children.length < childrenCount) {
        const suggestedTax = getSuggestedItemByAge(5, 'tax', taxes, defaultPosItems, 'child');
        const suggestedInsurance = getSuggestedItemByAge(5, 'insurance', taxes, defaultPosItems, 'child');
        newConfig.children.push({
            age: -1,
            visaInfoId: null,
            fixedTaxId: suggestedTax.id,
            fixedTaxId2: suggestedInsurance.id,
            fixedTaxType: suggestedTax.sourceType,
            checkInTimestamp: reservation.checkInTimestamp,
            checkOutTimestamp: reservation.checkOutTimestamp,
        });
        hasChanges = true;
    }
    
    // Popuni SAMO null/undefined za postojeću djecu
    for (let i = 0; i < newConfig.children.length; i++) {
        const child = newConfig.children[i];
        const childAge = child.age >= 0 ? child.age : 5;
        
        if (child.fixedTaxId === null || child.fixedTaxId === undefined) {
            const suggested = getSuggestedItemByAge(childAge, 'tax', taxes, defaultPosItems, 'child');
            // SAMO ako ima što postaviti!
            if (suggested.id !== null && suggested.id !== undefined) {
                newConfig.children[i].fixedTaxId = suggested.id;
                newConfig.children[i].fixedTaxType = suggested.sourceType;
                hasChanges = true;
            }
        }
        
        if (child.fixedTaxId && !child.fixedTaxType) {
            const isPosItem = defaultPosItems.some(p => p.posItemId === child.fixedTaxId);
            newConfig.children[i].fixedTaxType = isPosItem ? 'posItem' : 'tax';
            hasChanges = true;
        }
    }
    
    return hasChanges ? newConfig : null;
};

export interface TaxAmountInfo {
    amount: number | null;
    name: string | null;
}

/**
 * Dohvaća cijenu i ime iz lokalnih podataka (Tax ili PosItem)
 * Koristi se na frontendu - nema potrebe za async pozivima
 */
export const getTaxAmountInfoLocal = (
    taxId: number | null | undefined,
    taxType: 'tax' | 'posItem' | null | undefined,
    taxes: TaxModel[],
    posItems: DefaultPosItem[]
): TaxAmountInfo => {
    if (!taxId || taxId === 0) {
        return { amount: null, name: null };
    }

    if (taxType === 'posItem') {
        const posItem = posItems.find(p => p.posItemId === taxId);
        if (posItem) {
            return {
                amount: posItem.posItemPrice ? Number(posItem.posItemPrice) : null,
                name: posItem.posItemName || null,
            };
        }
    } else {
        // Default je 'tax'
        const tax = taxes.find(t => t.id === taxId);
        if (tax) {
            return {
                amount: tax.fixedAmount ? Number(tax.fixedAmount) : null,
                name: tax.name || null,
            };
        }
    }

    return { amount: null, name: null };
};